import { ChevronRight } from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { Drawer } from 'vaul';

import { Button } from '@/ux-common/components/button/button';
import { CheckboxGlyph } from '@/ux-common/components/checkbox/checkbox';
import { cn } from '@/ux-common/lib/utils';

/**
 * MobileMenu — the bottom-sheet counterpart of DropdownMenu, on `vaul` (a Radix
 * Dialog underneath, which is the only focus trap and the only dismiss layer). The
 * same 13 parts and prop semantics as DropdownMenu, with `Mobile` in the names.
 *
 * All visual styling is applied via CSS classes. The sheet chrome is `.mobile-menu*`.
 * The rows ARE DropdownMenu rows: the same `.dropdown-menu-*` classes and markup,
 * drawing the registry `.checkbox` / `.radio` in their inverse variant. The sheet
 * only rebinds their gap and inline padding. Change the two components together —
 * a test compares their row markup.
 *
 * Rows are hand-rolled, because Radix Menu items cannot run inside the sheet (they
 * bring a second focus trap and dismiss layer). They emit Radix Menu's attribute
 * contract (`role`, `aria-checked` incl. `"mixed"`, `aria-disabled`,
 * `data-highlighted`, `data-state`, `data-disabled`) and handle Enter, Space and the
 * arrow keys. There is no typeahead.
 *
 * Close semantics follow DropdownMenu: `MobileMenuItem` and `MobileMenuRadioItem`
 * close the whole sheet (also from inside a Sub), `MobileMenuCheckboxItem` keeps it
 * open, and `event.preventDefault()` in `onSelect` keeps it open. Swipe-down, Escape
 * and a backdrop tap dismiss it. To close it from other content (a CalendarPicker's
 * Apply), control `open` / `onOpenChange` on `MobileMenu`.
 *
 * A Sub is a stacked screen: `MobileMenuSubContent` replaces the screen it was opened
 * from, holds any content, and pins a Go-back button (`backLabel`) under its
 * scrolling body.
 *
 * Modifiers are classes, not props:
 * - `dropdown-menu-item-inset` indents a row by one control, for the children of a
 *   "select all" checkbox.
 * - A destructive command takes the consumer's `className="text-destructive"`.
 *
 * @example
 * ```tsx
 * <MobileMenu>
 *   <MobileMenuTrigger asChild>
 *     <DropdownButton className="btn-tier3">Options</DropdownButton>
 *   </MobileMenuTrigger>
 *   <MobileMenuContent title="Chart options">
 *     <MobileMenuLabel>View</MobileMenuLabel>
 *     <MobileMenuCheckboxItem checked={grid} onCheckedChange={setGrid}>
 *       Grid lines
 *     </MobileMenuCheckboxItem>
 *     <MobileMenuSeparator />
 *     <MobileMenuItem onSelect={exportCsv}>Export CSV</MobileMenuItem>
 *     <MobileMenuItem className="text-destructive" onSelect={removeChart}>
 *       Remove chart
 *     </MobileMenuItem>
 *   </MobileMenuContent>
 * </MobileMenu>
 * ```
 */

/* ================================================================
   Internals
   ================================================================ */

/** Every row (Item, CheckboxItem, RadioItem, SubTrigger): the keyboard's scope. */
const ITEM_SELECTOR = '[data-mobile-menu-item]';
/** A plain-`disabled` row: skipped by the arrow keys (a refused row stays reachable). */
const SKIP_ATTRIBUTE = 'data-mobile-menu-skip';
/** Every screen's `role="menu"` element. */
const SCREEN_SELECTOR = '[data-mobile-menu-screen]';
/** The select event a row dispatches on itself, as Radix's `menu.itemSelect`. */
const ITEM_SELECT = 'mobile-menu.itemSelect';

/**
 * Controlled-or-uncontrolled state, hand-rolled so the item does not pull in
 * `@radix-ui/react-use-controllable-state`. `onChange` runs only on a real change.
 */
function useControllableState<T>(
  prop: T | undefined,
  defaultProp: T,
  onChange: ((value: T) => void) | undefined
): [T, (next: T) => void] {
  const [uncontrolled, setUncontrolled] = React.useState(defaultProp);
  const controlled = prop !== undefined;
  const value = controlled ? prop : uncontrolled;
  const onChangeRef = React.useRef(onChange);
  React.useLayoutEffect(() => {
    onChangeRef.current = onChange;
  });
  const setValue = React.useCallback(
    (next: T) => {
      if (Object.is(next, value)) return;
      if (!controlled) setUncontrolled(next);
      onChangeRef.current?.(next);
    },
    [controlled, value]
  );
  return [value, setValue];
}

/**
 * Points a forwarded ref and a local ref at the same node. `MutableRefObject` (not
 * `RefObject`) so the vendored file also compiles against `@types/react` 18.
 */
function useComposedRef<T>(
  forwarded: React.ForwardedRef<T>,
  local: React.MutableRefObject<T | null>
) {
  return React.useCallback(
    (node: T | null) => {
      local.current = node;
      if (typeof forwarded === 'function') forwarded(node);
      else if (forwarded) forwarded.current = node;
    },
    [forwarded, local]
  );
}

/* ---------- Root ---------- */

type RootContextValue = {
  open: boolean;
  /** Closes the whole sheet, from any screen. */
  close: () => void;
};

const RootContext = React.createContext<RootContextValue | null>(null);

function useRootContext(part: string): RootContextValue {
  const context = React.useContext(RootContext);
  if (!context) throw new Error(`${part} must be used within MobileMenu`);
  return context;
}

/* ---------- Screens ---------- */

type ScreenContextValue = {
  /** Where this screen's Subs portal their SubContent. */
  host: HTMLElement | null;
  /** True while one of this screen's Subs is open: the screen is then hidden. */
  hidden: boolean;
  setChildOpen: (id: string, open: boolean) => void;
  /** The row that holds this screen's roving `tabIndex={0}`. */
  tabStop: string | null;
  setTabStop: (id: string) => void;
};

const ScreenContext = React.createContext<ScreenContextValue>({
  host: null,
  hidden: false,
  setChildOpen: () => {},
  tabStop: null,
  setTabStop: () => {},
});

function useScreen(host: HTMLElement | null): ScreenContextValue {
  const [openChildren, setOpenChildren] = React.useState<ReadonlySet<string>>(() => new Set());
  const [tabStop, setTabStop] = React.useState<string | null>(null);
  const setChildOpen = React.useCallback((id: string, open: boolean) => {
    setOpenChildren((previous) => {
      if (previous.has(id) === open) return previous;
      const next = new Set(previous);
      if (open) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);
  return React.useMemo(
    () => ({ host, hidden: openChildren.size > 0, setChildOpen, tabStop, setTabStop }),
    [host, openChildren, setChildOpen, tabStop]
  );
}

/** The rows that belong to this screen (not to a nested screen or custom menu). */
function rowsOf(menu: HTMLElement): HTMLElement[] {
  return Array.from(menu.querySelectorAll<HTMLElement>(ITEM_SELECTOR)).filter(
    (row) => row.closest('[role="menu"]') === menu
  );
}

/** Focuses the screen that is showing — on a normal open, the root menu. */
function focusVisibleScreen(sheet: HTMLElement | null) {
  if (!sheet) return;
  const screens = Array.from(sheet.querySelectorAll<HTMLElement>(SCREEN_SELECTOR)).filter(
    (screen) => !screen.closest('[hidden]')
  );
  screens[screens.length - 1]?.focus();
}

/**
 * The arrow keys of one screen. Acts only on that screen's own menu element or one
 * of its rows, so a nested screen (whose events bubble here through the React tree)
 * and custom content such as a CalendarPicker keep their keys. Enter and Space are
 * handled on each row; Tab, Escape and typeahead are not handled.
 */
function handleMenuKeyDown(event: React.KeyboardEvent<HTMLDivElement>, onBack?: () => void) {
  const menu = event.currentTarget;
  const target = event.target as HTMLElement;
  const onMenu = target === menu;
  if (!onMenu && !(target.matches(ITEM_SELECTOR) && target.closest('[role="menu"]') === menu)) {
    return;
  }

  const rows = rowsOf(menu);
  const enabled = rows.filter((row) => !row.hasAttribute(SKIP_ATTRIBUTE));
  const index = onMenu ? -1 : rows.indexOf(target);
  let next: HTMLElement | undefined;

  switch (event.key) {
    case 'ArrowDown':
      next = onMenu ? enabled[0] : enabled.find((row) => rows.indexOf(row) > index);
      break;
    case 'ArrowUp':
      next = onMenu
        ? enabled[enabled.length - 1]
        : [...enabled].reverse().find((row) => rows.indexOf(row) < index);
      break;
    case 'Home':
      next = enabled[0];
      break;
    case 'End':
      next = enabled[enabled.length - 1];
      break;
    case 'ArrowRight':
      if (!onMenu && target.getAttribute('aria-haspopup') === 'menu') {
        event.preventDefault();
        target.click();
      }
      return;
    case 'ArrowLeft':
      if (onBack) {
        event.preventDefault();
        onBack();
      }
      return;
    default:
      return;
  }

  // No loop: at either end the highlight stays where it is.
  event.preventDefault();
  next?.focus();
}

/* ---------- Radio ---------- */

type RadioContextValue = {
  value?: string;
  onValueChange?: (value: string) => void;
};

const RadioContext = React.createContext<RadioContextValue>({});

/* ---------- Sub ---------- */

type SubContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerId: string;
  contentId: string;
  /** True when the last input on the sub-screen was a pointer, not a key. */
  pointerInput: React.MutableRefObject<boolean>;
};

const SubContext = React.createContext<SubContextValue | null>(null);

function useSubContext(part: string): SubContextValue {
  const context = React.useContext(SubContext);
  if (!context) throw new Error(`${part} must be used within MobileMenuSub`);
  return context;
}

/* ---------- Refusal (copied from dropdown-menu.tsx) ----------
   Keep in step with DropdownMenu: `RefusableItemProps`, `blockClick`, `useRefusal`
   and `withReason` are copies of its module-private helpers (they are not exported,
   and MobileMenu does not depend on the dropdown-menu item). A change there is a
   change here. */

/** The prop every item takes on top of its row props. */
type RefusableItemProps = {
  /**
   * Refuses the item and says why. The row stays in the arrow-key order (WAI-ARIA
   * APG: a disabled menu item is focusable) but cannot be chosen, and the reason is
   * announced as its description — not shown: nothing is added to the row's look.
   * For a visible reason, wrap the item in a Tooltip
   * (`<TooltipTrigger asChild aria-describedby={undefined}>`). Plain `disabled`
   * skips the row instead — and a click on it calls nothing, not even your
   * `onClick`. Wins over `disabled` when both are set.
   */
  disabledReason?: React.ReactNode;
};

type RefusalInput = {
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  'aria-describedby'?: string;
};

/** Stands in for the consumer's onClick on a row that cannot be chosen. */
const blockClick = (event: React.MouseEvent<HTMLDivElement>) => event.preventDefault();

/**
 * The props that refuse an item, and the reason to render inside it — or nothing
 * when `disabledReason` is empty. The row's select runs only when its click is not
 * default-prevented, and Enter / Space reach it as a click, so `blockClick` stops a
 * refused or disabled row, its `onCheckedChange` / `onValueChange` and the close.
 * The reason is visually hidden (`sr-only`) and `aria-hidden`, so it is not read
 * again as part of the row's name; `aria-describedby` still reaches it.
 */
function useRefusal(disabledReason: React.ReactNode, props: RefusalInput) {
  const reasonId = React.useId();
  const refused = disabledReason != null && disabledReason !== false && disabledReason !== '';
  if (!refused) {
    return { itemProps: props.disabled ? { ...props, onClick: blockClick } : props, reason: null };
  }

  return {
    itemProps: {
      disabled: false,
      'aria-disabled': true,
      'data-disabled': '',
      'aria-describedby': [reasonId, props['aria-describedby']].filter(Boolean).join(' '),
      // The consumer's onClick is not called either: a refused row does nothing.
      onClick: blockClick,
    },
    reason: (
      <span id={reasonId} aria-hidden="true" className="sr-only">
        {disabledReason}
      </span>
    ),
  };
}

/**
 * A plain item's children with its reason after them. With no reason the children
 * pass through untouched. (MobileMenu items have no `asChild` yet; the branch is
 * kept so this stays a copy of DropdownMenu's.)
 */
function withReason(
  children: React.ReactNode,
  reason: React.ReactNode,
  asChild: boolean | undefined
): React.ReactNode {
  if (!reason) return children;
  if (asChild && React.isValidElement<{ children?: React.ReactNode }>(children)) {
    return React.cloneElement(children, undefined, children.props.children, reason);
  }
  return (
    <>
      {children}
      {reason}
    </>
  );
}

/* ---------- Rows ---------- */

/**
 * Dispatches the cancelable select event on the row, with `onSelect` as a one-shot
 * listener, and returns it so the caller can read `defaultPrevented`.
 */
function dispatchSelect(row: HTMLElement, onSelect?: (event: Event) => void): Event {
  const event = new CustomEvent(ITEM_SELECT, { bubbles: true, cancelable: true });
  if (onSelect) row.addEventListener(ITEM_SELECT, onSelect, { once: true });
  row.dispatchEvent(event);
  return event;
}

function menuOf(row: HTMLElement): HTMLElement | null {
  return row.closest<HTMLElement>('[role="menu"]');
}

type RowInput = {
  /** A plain-`disabled` row: skipped by the arrow keys; the pointer does not highlight it. */
  skip: boolean;
  /** Runs on a click that neither the consumer nor `blockClick` prevented. */
  onActivate: (row: HTMLDivElement) => void;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  onPointerMove?: React.PointerEventHandler<HTMLDivElement>;
  onPointerLeave?: React.PointerEventHandler<HTMLDivElement>;
};

/**
 * The behaviour every row shares: the roving tab stop, `data-highlighted` on focus,
 * mouse-only pointer highlight, Enter / Space as a click, and the composed click.
 * The consumer's handlers run first; a `preventDefault()` in them cancels ours.
 */
function useRow(input: RowInput) {
  const id = React.useId();
  const { tabStop, setTabStop } = React.useContext(ScreenContext);
  const [highlighted, setHighlighted] = React.useState(false);

  return {
    tabIndex: tabStop === id ? 0 : -1,
    'data-mobile-menu-item': '',
    'data-mobile-menu-skip': input.skip ? '' : undefined,
    'data-highlighted': highlighted ? '' : undefined,
    onClick: (event: React.MouseEvent<HTMLDivElement>) => {
      input.onClick?.(event);
      if (event.defaultPrevented) return;
      input.onActivate(event.currentTarget);
    },
    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
      input.onKeyDown?.(event);
      if (event.defaultPrevented || event.target !== event.currentTarget) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.currentTarget.click();
      }
    },
    onFocus: (event: React.FocusEvent<HTMLDivElement>) => {
      input.onFocus?.(event);
      if (event.target !== event.currentTarget) return;
      setHighlighted(true);
      setTabStop(id);
    },
    onBlur: (event: React.FocusEvent<HTMLDivElement>) => {
      input.onBlur?.(event);
      if (event.target !== event.currentTarget) return;
      setHighlighted(false);
    },
    onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => {
      input.onPointerMove?.(event);
      // Touch does nothing, so a tap leaves no sticky highlight.
      if (event.defaultPrevented || event.pointerType !== 'mouse') return;
      const row = event.currentTarget;
      if (input.skip) {
        const menu = menuOf(row);
        if (menu && document.activeElement !== menu) menu.focus();
        return;
      }
      if (document.activeElement !== row) row.focus({ preventScroll: true });
    },
    onPointerLeave: (event: React.PointerEvent<HTMLDivElement>) => {
      input.onPointerLeave?.(event);
      if (event.defaultPrevented || event.pointerType !== 'mouse') return;
      if (document.activeElement === event.currentTarget) menuOf(event.currentTarget)?.focus();
    },
  };
}

/** Row props: the div's, with `onSelect` replaced by the menu's select event. */
type RowBaseProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'onSelect'> & {
  /** Skips the row in the arrow-key order; nothing is called on click. */
  disabled?: boolean;
  /**
   * Called with a cancelable event when the row is chosen (click, Enter, Space).
   * `event.preventDefault()` keeps the sheet open.
   */
  onSelect?: (event: Event) => void;
};

/* ================================================================
   Root and trigger
   ================================================================ */

export type MobileMenuProps = {
  children?: React.ReactNode;
  /** Controlled open state. With `onOpenChange`, the way to close the sheet from content. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Passed to vaul. Default `true`: the page behind is inert while the sheet is open. */
  modal?: boolean;
};

/**
 * MobileMenu — the root. Renders no DOM. Wraps vaul's `Drawer.Root`, always
 * controlled, with `shouldScaleBackground` forced off. There is no
 * `useMobileMenu` hook: close the sheet from content through `open` /
 * `onOpenChange`.
 */
export function MobileMenu({
  children,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  modal = true,
}: MobileMenuProps) {
  const [open, setOpen] = useControllableState(openProp, defaultOpen, onOpenChange);
  const close = React.useCallback(() => setOpen(false), [setOpen]);
  const context = React.useMemo(() => ({ open, close }), [open, close]);

  return (
    <RootContext.Provider value={context}>
      <Drawer.Root open={open} onOpenChange={setOpen} modal={modal} shouldScaleBackground={false}>
        {children}
      </Drawer.Root>
    </RootContext.Provider>
  );
}

MobileMenu.displayName = 'MobileMenu';

export type MobileMenuTriggerProps = React.ComponentPropsWithoutRef<typeof Drawer.Trigger>;

/**
 * MobileMenuTrigger — vaul's `Drawer.Trigger` (a Radix Dialog trigger). Use
 * `asChild` with a registry `DropdownButton`; `disabled` keeps it closed. Under
 * `asChild` it adds no `data-component`, so the child keeps its own (a DropdownButton
 * reads `DropdownButton` on mobile as under DropdownMenuTrigger on desktop).
 */
export const MobileMenuTrigger = React.forwardRef<
  React.ElementRef<typeof Drawer.Trigger>,
  MobileMenuTriggerProps
>(({ asChild, ...props }, ref) => (
  <Drawer.Trigger
    ref={ref}
    asChild={asChild}
    data-component={asChild ? undefined : 'MobileMenuTrigger'}
    {...props}
  />
));

MobileMenuTrigger.displayName = 'MobileMenuTrigger';

/* ================================================================
   Content and screens
   ================================================================ */

/** The root screen. Unmounts with the sheet, so its state starts fresh on each open. */
function RootScreen({ titleId, children }: { titleId: string; children?: React.ReactNode }) {
  // The host is state, not a ref: a Sub that is open on the sheet's first render
  // re-renders once the host exists.
  const [host, setHost] = React.useState<HTMLDivElement | null>(null);
  const screen = useScreen(host);

  return (
    <>
      <ScreenContext.Provider value={screen}>
        <div
          role="menu"
          aria-orientation="vertical"
          aria-labelledby={titleId}
          tabIndex={-1}
          className="mobile-menu-body"
          data-mobile-menu-screen=""
          hidden={screen.hidden}
          onKeyDown={handleMenuKeyDown}
        >
          {children}
        </div>
      </ScreenContext.Provider>
      <div ref={setHost} className="mobile-menu-screens" />
    </>
  );
}

export type MobileMenuContentProps = Omit<
  React.ComponentPropsWithoutRef<typeof Drawer.Content>,
  'title'
> & {
  /** The dialog's accessible name, visually hidden. Default `"Menu"`. */
  title?: React.ReactNode;
};

/**
 * MobileMenuContent — the sheet: an overlay (`.mobile-menu-overlay`) and the panel
 * (`.mobile-menu`, `role="dialog"`), always in a portal. `className` goes on
 * `.mobile-menu`. Its children form the root screen, a scrolling
 * `.mobile-menu-body` with `role="menu"`. On open, focus moves to that menu and no
 * row is highlighted until an arrow key or the mouse reaches one.
 */
export const MobileMenuContent = React.forwardRef<HTMLDivElement, MobileMenuContentProps>(
  ({ className, children, title = 'Menu', onOpenAutoFocus, ...props }, ref) => {
    const sheetRef = React.useRef<HTMLDivElement | null>(null);
    const composedRef = useComposedRef(ref, sheetRef);
    const titleId = React.useId();

    return (
      <Drawer.Portal>
        <Drawer.Overlay className="mobile-menu-overlay" />
        <Drawer.Content
          ref={composedRef}
          data-component="MobileMenuContent"
          className={cn('mobile-menu', className)}
          aria-describedby={undefined}
          {...props}
          onOpenAutoFocus={(event) => {
            onOpenAutoFocus?.(event);
            if (event.defaultPrevented) return;
            // vaul cancels Radix's open focus (its autoFocus is off), which would leave
            // focus on the trigger behind the sheet. Focus the showing screen's menu.
            event.preventDefault();
            focusVisibleScreen(sheetRef.current);
          }}
        >
          <Drawer.Title className="sr-only">
            <span id={titleId}>{title}</span>
          </Drawer.Title>
          <RootScreen titleId={titleId}>{children}</RootScreen>
        </Drawer.Content>
      </Drawer.Portal>
    );
  }
);

MobileMenuContent.displayName = 'MobileMenuContent';

/* ================================================================
   Groups, label and separator
   ================================================================ */

export type MobileMenuGroupProps = React.ComponentPropsWithoutRef<'div'>;

/** MobileMenuGroup — a `<div role="group">` of rows (`.dropdown-menu-group`). */
export const MobileMenuGroup = React.forwardRef<HTMLDivElement, MobileMenuGroupProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      role="group"
      data-component="MobileMenuGroup"
      className={cn('dropdown-menu-group', className)}
      {...props}
    />
  )
);

MobileMenuGroup.displayName = 'MobileMenuGroup';

export type MobileMenuRadioGroupProps = React.ComponentPropsWithoutRef<'div'> & {
  /** The chosen option's value. `''` checks none. */
  value?: string;
  onValueChange?: (value: string) => void;
};

/**
 * MobileMenuRadioGroup — holds one choice among its `MobileMenuRadioItem`s. The
 * state travels by context, so RadioItems work inside fragments and child
 * components. Laid out like `MobileMenuGroup`.
 */
export const MobileMenuRadioGroup = React.forwardRef<HTMLDivElement, MobileMenuRadioGroupProps>(
  ({ className, value, onValueChange, ...props }, ref) => {
    const radio = React.useMemo(() => ({ value, onValueChange }), [value, onValueChange]);
    return (
      <RadioContext.Provider value={radio}>
        <div
          ref={ref}
          role="group"
          data-component="MobileMenuRadioGroup"
          className={cn('dropdown-menu-group', className)}
          {...props}
        />
      </RadioContext.Provider>
    );
  }
);

MobileMenuRadioGroup.displayName = 'MobileMenuRadioGroup';

export type MobileMenuLabelProps = React.ComponentPropsWithoutRef<'div'>;

/** MobileMenuLabel — a non-interactive group heading, set in caps. Not sticky. */
export const MobileMenuLabel = React.forwardRef<HTMLDivElement, MobileMenuLabelProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-component="MobileMenuLabel"
      className={cn('dropdown-menu-label', className)}
      {...props}
    />
  )
);

MobileMenuLabel.displayName = 'MobileMenuLabel';

export type MobileMenuSeparatorProps = React.ComponentPropsWithoutRef<'div'>;

/** MobileMenuSeparator — a 1px rule between groups. */
export const MobileMenuSeparator = React.forwardRef<HTMLDivElement, MobileMenuSeparatorProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      role="separator"
      aria-orientation="horizontal"
      data-component="MobileMenuSeparator"
      className={cn('dropdown-menu-separator', className)}
      {...props}
    />
  )
);

MobileMenuSeparator.displayName = 'MobileMenuSeparator';

/* ================================================================
   Items
   ================================================================ */

export type MobileMenuItemProps = RowBaseProps & RefusableItemProps;

/**
 * MobileMenuItem — one command (`role="menuitem"`). Pass a leading 16px glyph as the
 * first child and the text after it. Choosing it calls `onSelect` and closes the
 * whole sheet unless `onSelect` calls `event.preventDefault()`. A consumer `onClick`
 * runs first and cannot replace the select. `disabledReason` refuses it while
 * keeping it reachable. For a destructive command, add `className="text-destructive"`.
 */
export const MobileMenuItem = React.forwardRef<HTMLDivElement, MobileMenuItemProps>(
  (
    {
      children,
      className,
      disabled,
      disabledReason,
      onSelect,
      onClick,
      onKeyDown,
      onFocus,
      onBlur,
      onPointerMove,
      onPointerLeave,
      'aria-describedby': describedBy,
      ...props
    },
    ref
  ) => {
    const root = useRootContext('MobileMenuItem');
    const { itemProps, reason } = useRefusal(disabledReason, {
      disabled,
      onClick,
      'aria-describedby': describedBy,
    });
    const refused = reason !== null;
    const inactive = Boolean(disabled) || refused;
    const row = useRow({
      skip: Boolean(disabled) && !refused,
      onClick: itemProps.onClick,
      onKeyDown,
      onFocus,
      onBlur,
      onPointerMove,
      onPointerLeave,
      onActivate: (element) => {
        const event = dispatchSelect(element, onSelect);
        if (!event.defaultPrevented) root.close();
      },
    });

    return (
      <div
        ref={ref}
        role="menuitem"
        data-component="MobileMenuItem"
        className={cn('dropdown-menu-item', className)}
        {...props}
        {...row}
        aria-disabled={inactive || undefined}
        data-disabled={inactive ? '' : undefined}
        aria-describedby={itemProps['aria-describedby'] || undefined}
      >
        {withReason(children, reason, false)}
      </div>
    );
  }
);

MobileMenuItem.displayName = 'MobileMenuItem';

export type MobileMenuCheckboxItemProps = RowBaseProps &
  RefusableItemProps & {
    checked?: boolean | 'indeterminate';
    onCheckedChange?: (checked: boolean) => void;
  };

/**
 * MobileMenuCheckboxItem — a toggle row (`role="menuitemcheckbox"`). Draws the
 * registry `.checkbox` in its inverse variant; the row's `data-state` and
 * `data-disabled` drive its colours. `checked="indeterminate"` renders the minus
 * (`aria-checked="mixed"`), for a "select all" row whose children are partly checked;
 * choosing it then calls `onCheckedChange(true)`.
 *
 * Toggling keeps the sheet open, so several rows can be toggled in one visit (the
 * select event is prevented before your `onSelect`). `disabledReason` refuses it
 * while keeping it reachable.
 */
export const MobileMenuCheckboxItem = React.forwardRef<HTMLDivElement, MobileMenuCheckboxItemProps>(
  (
    {
      className,
      children,
      checked,
      onCheckedChange,
      disabled,
      disabledReason,
      onSelect,
      onClick,
      onKeyDown,
      onFocus,
      onBlur,
      onPointerMove,
      onPointerLeave,
      'aria-describedby': describedBy,
      ...props
    },
    ref
  ) => {
    const { itemProps, reason } = useRefusal(disabledReason, {
      disabled,
      onClick,
      'aria-describedby': describedBy,
    });
    const refused = reason !== null;
    const inactive = Boolean(disabled) || refused;
    const state = checked === 'indeterminate' ? 'indeterminate' : checked ? 'checked' : 'unchecked';
    const row = useRow({
      skip: Boolean(disabled) && !refused,
      onClick: itemProps.onClick,
      onKeyDown,
      onFocus,
      onBlur,
      onPointerMove,
      onPointerLeave,
      onActivate: (element) => {
        // Prevent first, so a consumer handler cannot undo the keep-open by
        // omission (DropdownMenuCheckboxItem does the same).
        dispatchSelect(element, (event) => {
          event.preventDefault();
          onSelect?.(event);
        });
        onCheckedChange?.(checked === 'indeterminate' ? true : !checked);
      },
    });

    return (
      <div
        ref={ref}
        role="menuitemcheckbox"
        aria-checked={checked === 'indeterminate' ? 'mixed' : Boolean(checked)}
        data-state={state}
        data-component="MobileMenuCheckboxItem"
        className={cn('dropdown-menu-item dropdown-menu-checkbox-item', className)}
        {...props}
        {...row}
        aria-disabled={inactive || undefined}
        data-disabled={inactive ? '' : undefined}
        aria-describedby={itemProps['aria-describedby'] || undefined}
      >
        {/* DropdownMenuCheckboxItem's markup: a decorative `.checkbox` span around
            Checkbox's own glyph. The indicator mounts only while checked or
            indeterminate, as Radix's ItemIndicator does. */}
        <span aria-hidden="true" className="checkbox checkbox-inverse">
          {state !== 'unchecked' && (
            <span className="checkbox-indicator" data-state={state}>
              <CheckboxGlyph checked={checked} />
            </span>
          )}
        </span>
        {children}
        {reason}
      </div>
    );
  }
);

MobileMenuCheckboxItem.displayName = 'MobileMenuCheckboxItem';

export type MobileMenuRadioItemProps = RowBaseProps &
  RefusableItemProps & {
    /** This option's value; the row is checked when the group's `value` equals it. */
    value: string;
  };

/**
 * MobileMenuRadioItem — one option of a `MobileMenuRadioGroup`
 * (`role="menuitemradio"`). Draws the registry `.radio` in its inverse variant; the
 * row's state drives it. Choosing it calls the group's `onValueChange(value)` and
 * closes the sheet unless `onSelect` calls `event.preventDefault()`.
 * `disabledReason` refuses it while keeping it reachable.
 */
export const MobileMenuRadioItem = React.forwardRef<HTMLDivElement, MobileMenuRadioItemProps>(
  (
    {
      className,
      children,
      value,
      disabled,
      disabledReason,
      onSelect,
      onClick,
      onKeyDown,
      onFocus,
      onBlur,
      onPointerMove,
      onPointerLeave,
      'aria-describedby': describedBy,
      ...props
    },
    ref
  ) => {
    const root = useRootContext('MobileMenuRadioItem');
    const radio = React.useContext(RadioContext);
    const checked = radio.value === value;
    const { itemProps, reason } = useRefusal(disabledReason, {
      disabled,
      onClick,
      'aria-describedby': describedBy,
    });
    const refused = reason !== null;
    const inactive = Boolean(disabled) || refused;
    const row = useRow({
      skip: Boolean(disabled) && !refused,
      onClick: itemProps.onClick,
      onKeyDown,
      onFocus,
      onBlur,
      onPointerMove,
      onPointerLeave,
      onActivate: (element) => {
        const event = dispatchSelect(element, onSelect);
        radio.onValueChange?.(value);
        if (!event.defaultPrevented) root.close();
      },
    });

    return (
      <div
        ref={ref}
        role="menuitemradio"
        aria-checked={checked}
        data-state={checked ? 'checked' : 'unchecked'}
        data-component="MobileMenuRadioItem"
        className={cn('dropdown-menu-item dropdown-menu-radio-item', className)}
        {...props}
        {...row}
        aria-disabled={inactive || undefined}
        data-disabled={inactive ? '' : undefined}
        aria-describedby={itemProps['aria-describedby'] || undefined}
      >
        <span aria-hidden="true" className="radio radio-inverse">
          {checked && <span className="radio-indicator" data-state="checked" />}
        </span>
        {children}
        {reason}
      </div>
    );
  }
);

MobileMenuRadioItem.displayName = 'MobileMenuRadioItem';

/* ================================================================
   Sub (stacked screen)
   ================================================================ */

export type MobileMenuSubProps = {
  children?: React.ReactNode;
  /** Controlled open state — e.g. to go back from a CalendarPicker's Cancel. */
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * MobileMenuSub — a `MobileMenuSubTrigger` and the `MobileMenuSubContent` it opens.
 * Renders no DOM. While open, the screen it sits in is hidden. Closing the sheet
 * closes it (`onOpenChange(false)`), so it reopens on the root screen.
 */
export function MobileMenuSub({
  children,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
}: MobileMenuSubProps) {
  const root = useRootContext('MobileMenuSub');
  const { setChildOpen } = React.useContext(ScreenContext);
  const [open, setOpen] = useControllableState(openProp, defaultOpen, onOpenChange);
  const id = React.useId();
  const triggerId = React.useId();
  const contentId = React.useId();

  React.useLayoutEffect(() => {
    if (!root.open) setOpen(false);
  }, [root.open, setOpen]);

  // The reset above runs as the sheet starts to close, but vaul keeps the sheet
  // mounted through its slide-down. Keep showing the screen the user was on until
  // the sheet unmounts, or the parent screen would flash in on the way out. A
  // reopen before the sheet unmounts shows the reset state again.
  const openWhileRootOpen = React.useRef(open);
  React.useLayoutEffect(() => {
    if (root.open) openWhileRootOpen.current = open;
  });
  const shown = root.open ? open : openWhileRootOpen.current;

  React.useLayoutEffect(() => {
    setChildOpen(id, shown);
  }, [id, shown, setChildOpen]);

  React.useLayoutEffect(() => () => setChildOpen(id, false), [id, setChildOpen]);

  const pointerInput = React.useRef(false);

  const context = React.useMemo(
    () => ({ open: shown, setOpen, triggerId, contentId, pointerInput }),
    [shown, setOpen, triggerId, contentId]
  );

  return <SubContext.Provider value={context}>{children}</SubContext.Provider>;
}

MobileMenuSub.displayName = 'MobileMenuSub';

export type MobileMenuSubTriggerProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'onSelect'> & {
  /** Skips the row in the arrow-key order; it does not open. */
  disabled?: boolean;
};

/**
 * MobileMenuSubTrigger — a row that opens its Sub's screen (click, Enter, Space or
 * ArrowRight). Renders the trailing chevron; `aria-expanded`, `aria-controls` and
 * `data-state` follow the Sub. When the Sub closes it takes focus back.
 */
export const MobileMenuSubTrigger = React.forwardRef<HTMLDivElement, MobileMenuSubTriggerProps>(
  (
    {
      className,
      children,
      disabled,
      onClick,
      onKeyDown,
      onFocus,
      onBlur,
      onPointerMove,
      onPointerLeave,
      ...props
    },
    ref
  ) => {
    const root = useRootContext('MobileMenuSubTrigger');
    const sub = useSubContext('MobileMenuSubTrigger');
    const screen = React.useContext(ScreenContext);
    const localRef = React.useRef<HTMLDivElement | null>(null);
    const composedRef = useComposedRef(ref, localRef);
    const row = useRow({
      skip: Boolean(disabled),
      onClick: disabled ? blockClick : onClick,
      onKeyDown,
      onFocus,
      onBlur,
      onPointerMove,
      onPointerLeave,
      onActivate: () => sub.setOpen(true),
    });

    // Going back refocuses this screen, but only when focus was inside the closing
    // screen (its DOM is gone, so focus is now on body) and the sheet stays open.
    // This screen un-hides one commit after the Sub closes, so wait for it. A back
    // by key focuses this trigger; a back by tap focuses the menu, as an open does,
    // so the trigger is not left drawn highlighted.
    const wasOpen = React.useRef(sub.open);
    const refocus = React.useRef(false);
    React.useLayoutEffect(() => {
      if (wasOpen.current && !sub.open && root.open) {
        const active = document.activeElement;
        refocus.current = active === null || active === document.body;
      }
      wasOpen.current = sub.open;
      if (refocus.current && !sub.open && !screen.hidden) {
        refocus.current = false;
        const trigger = localRef.current;
        if (!trigger) return;
        if (sub.pointerInput.current) menuOf(trigger)?.focus();
        else trigger.focus();
      }
    }, [sub.open, sub.pointerInput, screen.hidden, root.open]);

    return (
      <div
        ref={composedRef}
        id={sub.triggerId}
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={sub.open}
        aria-controls={sub.open ? sub.contentId : undefined}
        data-state={sub.open ? 'open' : 'closed'}
        data-component="MobileMenuSubTrigger"
        className={cn('dropdown-menu-item dropdown-menu-sub-trigger', className)}
        {...props}
        {...row}
        aria-disabled={disabled || undefined}
        data-disabled={disabled ? '' : undefined}
      >
        {children}
        <ChevronRight aria-hidden="true" className="dropdown-menu-chevron" />
      </div>
    );
  }
);

MobileMenuSubTrigger.displayName = 'MobileMenuSubTrigger';

export type MobileMenuSubContentProps = React.ComponentPropsWithoutRef<'div'> & {
  /** The Go-back button's label. Default `"Go back"`. */
  backLabel?: React.ReactNode;
};

/** One open sub-screen. Mounts when its Sub opens, so its state starts fresh. */
const SubScreen = React.forwardRef<HTMLDivElement, MobileMenuSubContentProps>(
  ({ className, children, backLabel = 'Go back', ...props }, ref) => {
    const sub = useSubContext('MobileMenuSubContent');
    const [host, setHost] = React.useState<HTMLDivElement | null>(null);
    const screen = useScreen(host);
    const bodyRef = React.useRef<HTMLDivElement | null>(null);
    const back = () => sub.setOpen(false);

    // A newly shown screen focuses its first enabled row, or its menu if it has none.
    React.useLayoutEffect(() => {
      const body = bodyRef.current;
      if (!body) return;
      const first = rowsOf(body).find((row) => !row.hasAttribute(SKIP_ATTRIBUTE));
      (first ?? body).focus();
      sub.pointerInput.current = false;
    }, [sub.pointerInput]);

    return (
      <>
        <div
          ref={ref}
          data-component="MobileMenuSubContent"
          className={cn('mobile-menu-sub-content', className)}
          {...props}
          hidden={screen.hidden}
          onPointerDownCapture={(event) => {
            props.onPointerDownCapture?.(event);
            sub.pointerInput.current = true;
          }}
          onKeyDownCapture={(event) => {
            props.onKeyDownCapture?.(event);
            sub.pointerInput.current = false;
          }}
        >
          <ScreenContext.Provider value={screen}>
            <div
              ref={bodyRef}
              id={sub.contentId}
              role="menu"
              aria-orientation="vertical"
              aria-labelledby={sub.triggerId}
              tabIndex={-1}
              className="mobile-menu-body"
              data-mobile-menu-screen=""
              onKeyDown={(event) => handleMenuKeyDown(event, back)}
            >
              {children}
            </div>
          </ScreenContext.Provider>
          <div className="mobile-menu-footer">
            <Button className="btn-inverse btn-lg mobile-menu-back" onClick={back}>
              {backLabel}
            </Button>
          </div>
        </div>
        {/* This screen's own host, outside the element it hides when a nested Sub opens. */}
        <div ref={setHost} className="mobile-menu-screens" />
      </>
    );
  }
);

SubScreen.displayName = 'MobileMenuSubScreen';

/**
 * MobileMenuSubContent — the Sub's stacked screen, portalled into the sheet in place
 * of the screen it was opened from: a scrolling `.mobile-menu-body`
 * (`role="menu"`, named by the SubTrigger) that holds any content — rows, or a
 * CalendarPicker — above a pinned Go-back button (`backLabel`). `className` goes on
 * `.mobile-menu-sub-content`. ArrowLeft on a row goes back.
 */
export const MobileMenuSubContent = React.forwardRef<HTMLDivElement, MobileMenuSubContentProps>(
  (props, ref) => {
    const sub = useSubContext('MobileMenuSubContent');
    const { host } = React.useContext(ScreenContext);
    if (!sub.open || !host) return null;
    return createPortal(<SubScreen ref={ref} {...props} />, host);
  }
);

MobileMenuSubContent.displayName = 'MobileMenuSubContent';
